/**
 * Bloc image — widget de mise en page, sans données ni requête.
 *
 * Sécurité : l'URL est saisie par l'utilisateur du rapport et n'est JAMAIS injectée
 * en HTML. On rend une balise `<img src>` et rien d'autre, et on valide le protocole
 * en amont : seuls `http:`, `https:`, les chemins relatifs et les `data:image/...`
 * sont acceptés. Un `javascript:` (ou tout autre schéma) rend un état vide.
 */
import type { WidgetConfig } from '../schemas';

type Row = Record<string, string | number>;

interface WidgetProps {
  config: WidgetConfig;
  data: { current: Row[]; previous: Row[] | null; meta: any } | null;
  loading?: boolean;
  error?: string;
}

// Cadre fourni par `WidgetShell` (WidgetRenderer) : ici, hauteur seulement.
const SHELL = 'h-full';

/** Vrai si l'URL peut être posée sans risque dans `<img src>`. */
export function isSafeImageUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  const raw = url.trim();
  if (!raw) return false;

  let parsed: URL;
  try {
    // Base = origine courante : une URL relative (`/uploads/x.png`) devient http(s).
    parsed = new URL(raw, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
  } catch {
    return false;
  }

  const protocol = parsed.protocol.toLowerCase();
  if (protocol === 'http:' || protocol === 'https:') return true;
  // `data:` restreint aux images : un `data:text/html` ou `data:image/svg+xml` peut
  // porter du script. On exclut donc aussi le SVG.
  if (protocol === 'data:') {
    return /^data:image\/(png|jpeg|jpg|gif|webp|avif);/i.test(raw);
  }
  return false;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className={`${SHELL} flex items-center justify-center p-4`}>
      <span className="text-sm text-[#8B8FA7] italic">{message}</span>
    </div>
  );
}

export default function ImageBlockWidget({ config, loading, error }: WidgetProps) {
  if (loading) {
    return (
      <div className={`${SHELL} p-2`}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${SHELL} flex items-center justify-center p-4`}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const display = (config.display ?? {}) as Record<string, unknown>;
  const url = display.url as string | undefined;
  const alt = (display.alt as string | undefined) ?? '';
  const objectFit = (display.object_fit as 'cover' | 'contain' | undefined) ?? 'cover';

  if (!url || !String(url).trim()) return <EmptyState message="Aucune image" />;
  if (!isSafeImageUrl(url)) return <EmptyState message="Image non valide (URL refusée)" />;

  return (
    // `overflow-hidden rounded-xl` : l'image doit épouser les coins arrondis du cadre.
    <div className={`${SHELL} overflow-hidden rounded-xl`}>
      <img src={url.trim()} alt={alt} className="w-full h-full" style={{ objectFit }} />
    </div>
  );
}
